// src/config/subscription.js
//
// Single source of truth for subscription gating logic (Phase 1). Pure, framework-
// free helpers so they are trivially unit-testable. The shape mirrors Stripe so a
// Phase 2 webhook can write `companies/{id}.subscription` verbatim; nothing here
// cares who wrote it (dev simulator now, Stripe later).
//
// See PRD/PRD_Billing.md.
import { PERMISSIONS } from './permissions';
import { PREDEFINED_ROLES } from './roles';

// Tunables
export const GRACE_DAYS = 7;           // past_due window still treated as valid
export const TRIAL_WARNING_DAYS = 3;   // trial banner escalates at/below this

// Stripe subscription statuses we recognize
export const SUBSCRIPTION_STATUS = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  UNPAID: 'unpaid',
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  PAUSED: 'paused',
};

// Internal phases the app gates on
export const PHASE = {
  TRIAL: 'trial',
  ACTIVE: 'active',
  GRACE: 'grace',
  EXPIRED: 'expired',
};

// The permission that identifies a billing-capable superuser (set:bill:edit).
// Non-delegatable + carried only by the predefined `superuser` role, so
// "holds this" === "is a superuser".
export const BILLING_PERMISSION = PERMISSIONS.SETTINGS_PAYMENTS;

// Permissions a clamped superuser keeps in read-only (expired) mode:
// the viewer view-perms ∪ billing ∪ PII view (so the owner can see/export full
// records for compliance). viewer omits PII + set:bill:view, so union them in.
export const READ_ONLY_ALLOWED = new Set([
  ...PREDEFINED_ROLES.viewer.permissions,
  PERMISSIONS.SETTINGS_PAYMENTS,        // set:bill:edit
  PERMISSIONS.SETTINGS_PAYMENTS_VIEW,   // set:bill:view
  PERMISSIONS.EMPLOYEES_PII_VIEW,       // emp:pii:view
]);

// Normalize a Firestore Timestamp | Date | millis | ISO string → ms (or null).
const toMillis = (v) => {
  if (v == null) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();        // Firestore Timestamp
  if (typeof v.toDate === 'function') return v.toDate().getTime();  // Timestamp (older)
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * Resolve the internal phase from a normalized subscription object.
 * A missing/blank subscription → 'trial' (safety net: never hard-lock on read,
 * so existing companies with no field are unaffected). Phase 2 adds two purely
 * additive rules (Step 10): trial-expiry for grandfathered trials, and `paused`
 * grace (C1). The grandfathered-statusless and `past_due` branches are preserved.
 *
 * The platform app ports this rule for rule (chetgo/sahod-platform
 * src/lib/subscriptionPhase.js, PRD_CompaniesList). Change one, change the other.
 */
export const getSubscriptionPhase = (subscription, now = Date.now()) => {
  if (!subscription || !subscription.status) return PHASE.TRIAL;
  switch (subscription.status) {
    case SUBSCRIPTION_STATUS.TRIALING: {
      // A Phase-1 / grandfathered trial that ran out WITHOUT ever converting to a
      // Stripe subscription is expired. A real Stripe trial (has a subscription id)
      // stays TRIAL — Stripe drives its end via status transitions (→ active /
      // past_due / paused), so we never expire it on trialEnd alone.
      const trialEnds = toMillis(subscription.trialEnd);
      if (trialEnds != null && trialEnds < now && !subscription.stripeSubscriptionId) {
        return PHASE.EXPIRED;
      }
      return PHASE.TRIAL;
    }
    case SUBSCRIPTION_STATUS.ACTIVE:
      return PHASE.ACTIVE;
    case SUBSCRIPTION_STATUS.PAST_DUE:
    case SUBSCRIPTION_STATUS.PAUSED: {
      // past_due (active-sub payment failure) and paused (trial-end-without-payment-
      // method, C1) share the synthesized graceEndsAt window: within grace → GRACE,
      // after it / no grace → EXPIRED.
      const graceEnds = toMillis(subscription.graceEndsAt);
      return graceEnds != null && now < graceEnds ? PHASE.GRACE : PHASE.EXPIRED;
    }
    default:
      // canceled / unpaid / incomplete / incomplete_expired / unknown
      return PHASE.EXPIRED;
  }
};

export const isValidPhase = (phase) =>
  phase === PHASE.TRIAL || phase === PHASE.ACTIVE || phase === PHASE.GRACE;

/** Whether a raw permission set carries billing capability (≡ is a superuser). */
export const isBillingCapable = (rawPermissions) =>
  Array.isArray(rawPermissions) && rawPermissions.includes(BILLING_PERMISSION);

/**
 * The read-only clamp, as a pure function (the load-bearing gating logic).
 * Clamps ONLY when the subscription is expired AND the holder is billing-capable
 * (a superuser); everyone else keeps their raw permissions — non-superusers are
 * blocked at the UI layer, not clamped. Returns the SAME array reference when not
 * clamped, so callers can use identity to detect a no-op (and reversibility is
 * free: feeding a valid subscription again yields the original reference).
 */
export const computeEffectivePermissions = (rawPermissions, subscription, now = Date.now()) => {
  const raw = Array.isArray(rawPermissions) ? rawPermissions : [];
  const readOnly =
    getSubscriptionPhase(subscription, now) === PHASE.EXPIRED && isBillingCapable(raw);
  if (!readOnly) return raw;
  return raw.filter((p) => READ_ONLY_ALLOWED.has(p));
};

export const isSubscriptionValid = (subscription, now = Date.now()) =>
  isValidPhase(getSubscriptionPhase(subscription, now));

/**
 * Whole days remaining until the deadline relevant to the phase
 * (trial → trialEnd, grace → graceEndsAt). null when no relevant date exists
 * (e.g. a grandfathered company with no trialEnd → banner suppressed).
 */
export const getDaysLeft = (subscription, phase, now = Date.now()) => {
  if (!subscription) return null;
  let target = null;
  if (phase === PHASE.TRIAL) target = toMillis(subscription.trialEnd);
  else if (phase === PHASE.GRACE) target = toMillis(subscription.graceEndsAt);
  if (target == null) return null;
  return Math.max(0, Math.ceil((target - now) / 86400000));
};
